<template>
    <div class="mobile-payment-page">
        <HeaderMobile />
        
        <div class="payment-container">
            <!-- 支付进度 -->
            <div class="payment-step">
                <div class="step-item" :class="{ active: ['pending', 'processing', 'success'].includes(paymentStatus) }">
                    <span class="step-num">1</span>
                    <span class="step-text">确认订单</span>
                </div>
                <div class="step-line" :class="{ active: ['processing', 'success'].includes(paymentStatus) }"></div>
                <div class="step-item" :class="{ active: ['processing', 'success'].includes(paymentStatus) }">
                    <span class="step-num">2</span>
                    <span class="step-text">完成支付</span>
                </div>
                <div class="step-line" :class="{ active: paymentStatus === 'success' }"></div>
                <div class="step-item" :class="{ active: paymentStatus === 'success' }">
                    <span class="step-num">✓</span>
                    <span class="step-text">支付成功</span>
                </div>
            </div>

            <!-- 课程信息 -->
            <div class="course-card" v-if="courseInfo.course">
                <img :src="getImageUrl(courseInfo.course.cover)" alt="课程封面" @error="handleCoverError">
                <div class="course-info">
                    <h3>{{ courseInfo.course.name }}</h3>
                    <p><i class="el-icon-user"></i>{{ courseInfo.author.name }}</p>
                </div>
            </div>

            <!-- 待支付 -->
            <div class="payment-card" v-if="paymentStatus === 'pending'">
                <div class="countdown-tip">
                    <i class="el-icon-clock"></i>
                    支付剩余时间：<span>{{ countdownText }}</span>
                </div>
                <div class="payment-amount">
                    <span>应付金额：</span>
                    <span class="amount">¥{{ parseFloat(courseInfo.course.price).toFixed(2) }}</span>
                </div>
                <div class="qrcode-wrap">
                    <p>请使用微信扫码支付</p>
                    <img :src="qrcodeUrl" alt="支付二维码">
                    <p class="qrcode-tip"><i class="el-icon-info"></i>扫码后请等待页面自动跳转</p>
                </div>
                <div class="payment-actions">
                    <el-button @click="goBack">取消支付</el-button>
                    <el-button type="primary" @click="checkPayStatus">已支付，刷新状态</el-button>
                </div>
            </div>

            <!-- 支付中 -->
            <div class="payment-card" v-if="paymentStatus === 'processing'">
                <div class="processing-wrap">
                    <i class="el-icon-loading"></i>
                    <h3>正在确认支付信息</h3>
                    <p>请稍候...</p>
                </div>
            </div>

            <!-- 支付成功 -->
            <div class="payment-card" v-if="paymentStatus === 'success'">
                <div class="success-wrap">
                    <i class="el-icon-circle-check"></i>
                    <h3>支付成功</h3>
                    <p>您已成功购买《{{ courseInfo.course.name }}》</p>
                    <p class="order-number">订单编号：<strong>{{ courseInfo.payinfo.order_no }}</strong></p>
                    <div class="success-actions">
                        <el-button type="primary" @click="goToCourse">立即学习</el-button>
                        <el-button @click="goBackHome">返回首页</el-button>
                    </div>
                </div>
            </div>

            <!-- 支付失败 -->
            <div class="payment-card" v-if="paymentStatus === 'failed'">
                <div class="failed-wrap">
                    <i class="el-icon-circle-close"></i>
                    <h3>支付失败</h3>
                    <p>请检查支付方式或重新扫码支付</p>
                    <div class="failed-actions">
                        <el-button type="primary" @click="retryPay">重新支付</el-button>
                    </div>
                </div>
            </div>
        </div>

        <FooterMobile />
    </div>
</template>

<script>
import HeaderMobile from '@/components/mobile/header.vue';
import FooterMobile from '@/components/mobile/fotter.vue';
import { getCoursePayInfo, getPayStatus } from '@/api/home';
import { Session } from '@/utils/storage';

export default {
    name: 'MobilePaymentPage',
    components: { HeaderMobile, FooterMobile },
    data() {
        return {
            courseInfo: {},
            qrcodeUrl: '',
            countdown: 300,
            countdownText: '',
            countdownTimer: null,
            paymentStatus: 'pending',
            checkPayStatusTimer: null
        };
    },
    created() {
        if (!Session.get('token')) {
            this.$message.warning('请先登录');
            this.$router.push('/');
            return;
        }
        const courseId = this.$route.query.course_id;
        if (!courseId) {
            this.$message.error('课程信息不存在');
            this.$router.push('/');
            return;
        }
        this.fetchCourseInfo(courseId);
    },
    beforeDestroy() {
        clearInterval(this.countdownTimer);
        clearInterval(this.checkPayStatusTimer);
    },
    methods: {
        fetchCourseInfo(id) {
            getCoursePayInfo({ id }).then(res => {
                if (res.code && res.data) {
                    this.courseInfo = res.data;
                    this.qrcodeUrl = process.env.VUE_APP_STATIC_URL + res.data.qrcode_image || '';
                    this.countdown = res.data.timeout || 300;
                    this.initCountdown();
                    this.checkPayStatusTimer = setInterval(() => this.checkPayStatus(), 5000);
                } else {
                    this.$message.error(res.msg || '课程信息获取失败');
                    this.$router.push('/');
                }
            });
        },
        initCountdown() {
            this.updateCountdownText();
            this.countdownTimer = setInterval(() => {
                if (this.countdown <= 0) {
                    clearInterval(this.countdownTimer);
                    this.paymentStatus = 'failed';
                    this.$message.warning('支付超时');
                    return;
                }
                this.countdown--;
                this.updateCountdownText();
            }, 1000);
        },
        updateCountdownText() {
            const min = Math.floor(this.countdown / 60);
            const sec = this.countdown % 60;
            this.countdownText = `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
        },
        checkPayStatus() {
            if (this.paymentStatus !== 'pending') return;
            getPayStatus({ order_no: this.courseInfo.payinfo.order_no }).then(res => {
                if (res.code && res.data) {
                    if (res.data.status === 'succ') {
                        this.paymentStatus = 'success';
                        this.clearAllTimers();
                        this.$message.success('支付成功！');
                    } else if (res.data.status === 'cancel') {
                        this.paymentStatus = 'failed';
                        this.clearAllTimers();
                    }
                }
            });
        },
        clearAllTimers() {
            clearInterval(this.countdownTimer);
            clearInterval(this.checkPayStatusTimer);
        },
        goBack() {
            this.$router.go(-1);
        },
        goToCourse() {
            this.$router.push(`/mobile/course/${this.courseInfo.course.id}`);
        },
        goBackHome() {
            this.$router.push('/mobile');
        },
        retryPay() {
            this.paymentStatus = 'pending';
            this.countdown = 300;
            this.initCountdown();
            this.checkPayStatusTimer = setInterval(() => this.checkPayStatus(), 5000);
        },
        getImageUrl(url) {
            if (!url) return require('@/static/imgs/default.jpeg');
            if (url.startsWith('http://') || url.startsWith('https://')) return url;
            return process.env.VUE_APP_STATIC_URL + url;
        },
        handleCoverError(e) {
            e.target.src = require('@/static/imgs/default.jpeg');
        }
    }
};
</script>

<style scoped>
.mobile-payment-page { min-height: 100vh; background: #f5f5f5; padding-top: 60px; }
.payment-container { padding: 15px; }
.payment-step { display: flex; align-items: center; justify-content: center; margin-bottom: 20px; background: #fff; padding: 15px; border-radius: 8px; }
.step-item { display: flex; flex-direction: column; align-items: center; }
.step-num { width: 28px; height: 28px; border-radius: 50%; background: #e5e5e5; color: #999; display: flex; align-items: center; justify-content: center; font-size: 12px; margin-bottom: 4px; }
.step-item.active .step-num { background: #1890ff; color: #fff; }
.step-text { font-size: 10px; color: #999; }
.step-item.active .step-text { color: #1890ff; }
.step-line { width: 30px; height: 2px; background: #e5e5e5; margin: 0 8px; }
.step-line.active { background: #1890ff; }
.course-card { background: #fff; border-radius: 8px; padding: 15px; margin-bottom: 15px; display: flex; gap: 12px; }
.course-card img { width: 80px; height: 60px; border-radius: 4px; object-fit: cover; }
.course-info { flex: 1; }
.course-info h3 { font-size: 14px; margin-bottom: 8px; }
.course-info p { font-size: 12px; color: #666; }
.payment-card { background: #fff; border-radius: 8px; padding: 20px; }
.countdown-tip { text-align: center; font-size: 12px; color: #f56c6c; margin-bottom: 15px; }
.countdown-tip span { font-weight: 600; }
.payment-amount { text-align: center; font-size: 14px; margin-bottom: 20px; }
.payment-amount .amount { font-size: 24px; font-weight: 700; color: #f56c6c; margin-left: 8px; }
.qrcode-wrap { text-align: center; margin-bottom: 20px; }
.qrcode-wrap p { font-size: 12px; color: #666; margin-bottom: 12px; }
.qrcode-wrap img { width: 180px; height: 180px; border: 1px solid #e5e5e5; padding: 8px; border-radius: 8px; }
.qrcode-tip { font-size: 10px; color: #999; margin-top: 12px; }
.payment-actions { display: flex; gap: 10px; }
.payment-actions button { flex: 1; }
.processing-wrap, .success-wrap, .failed-wrap { text-align: center; padding: 40px 20px; }
.processing-wrap i { font-size: 48px; color: #1890ff; margin-bottom: 16px; animation: rotate 1.5s linear infinite; }
@keyframes rotate { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
.success-wrap i { font-size: 64px; color: #52c41a; margin-bottom: 16px; }
.failed-wrap i { font-size: 64px; color: #f56c6c; margin-bottom: 16px; }
.processing-wrap h3, .success-wrap h3, .failed-wrap h3 { font-size: 18px; margin-bottom: 8px; }
.processing-wrap p, .success-wrap p, .failed-wrap p { font-size: 14px; color: #666; margin-bottom: 20px; }
.success-actions, .failed-actions { display: flex; flex-direction: column; gap: 10px; }
.order-number { font-size: 12px !important; color: #999; margin-top: 4px !important; }
</style>
